import React, { useState } from 'react';
import { Bell, X, Send, Loader, Users, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';

const API_BASE = window.location.origin;

export default function QuickNotifFAB() {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const [open, setOpen]     = useState(false);
  const [title, setTitle]   = useState('');
  const [body, setBody]     = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent]     = useState(false);

  // Admin only
  if (!user || user.role !== 'admin') return null;

  const isDarkBg  = isDark ? 'rgba(15,23,42,0.97)' : 'rgba(255,255,255,0.98)';
  const borderClr = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.10)';
  const textPri   = isDark ? '#f9fafb' : '#0f172a';
  const textMut   = isDark ? '#6b7280' : '#94a3b8';
  const inputBg   = isDark ? 'rgba(255,255,255,0.05)' : '#f8fafc';
  const inputBdr  = isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.12)';

  const handleSend = async () => {
    if (!title.trim() || !body.trim()) {
      toast.error('أدخل العنوان والرسالة');
      return;
    }
    setSending(true);
    try {
      const token = localStorage.getItem('rtm_auth_token') || localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/notifications/broadcast`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ title: title.trim(), body: body.trim(), target: 'all' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'فشل الإرسال');
      setSent(true);
      toast.success(`أُرسل إلى ${data.success_count ?? '?'} مستخدم`);
      setTimeout(() => {
        setSent(false);
        setTitle('');
        setBody('');
        setOpen(false);
      }, 2000);
    } catch (err) {
      toast.error(err.message || 'حدث خطأ أثناء الإرسال');
    }
    setSending(false);
  };

  return (
    <>
      {/* ── Floating Bell Button ── */}
      <button
        onClick={() => setOpen(true)}
        data-testid="quick-notif-fab"
        className="fixed z-[60] flex items-center justify-center rounded-full shadow-2xl transition-all duration-200 active:scale-90 md:bottom-8 md:left-8"
        style={{
          bottom: 80,
          left: 16,
          width: 48,
          height: 48,
          background: 'linear-gradient(135deg, #eab308 0%, #f59e0b 100%)',
          boxShadow: '0 8px 24px rgba(234,179,8,0.45), 0 2px 8px rgba(0,0,0,0.3)',
        }}
        title="إرسال إشعار سريع"
      >
        <Bell className="w-5 h-5 text-black" />
      </button>

      {/* ── Bottom Sheet Modal ── */}
      {open && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm"
            onClick={() => setOpen(false)}
          />

          {/* Sheet */}
          <div
            data-testid="quick-notif-modal"
            className="fixed z-[75] left-0 right-0 bottom-0 rounded-t-3xl"
            style={{
              background: isDarkBg,
              border: `1px solid ${borderClr}`,
              borderBottom: 'none',
              backdropFilter: 'blur(24px)',
              padding: '20px 20px 32px',
              fontFamily: 'Tajawal, sans-serif',
            }}
          >
            {/* Handle */}
            <div className="w-10 h-1 rounded-full mx-auto mb-4" style={{ background: isDark ? '#374151' : '#e2e8f0' }} />

            {/* Header */}
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl flex items-center justify-center"
                     style={{ background: 'rgba(234,179,8,0.15)' }}>
                  <Bell className="w-4 h-4" style={{ color: '#eab308' }} />
                </div>
                <div>
                  <p className="text-sm font-black" style={{ color: textPri }}>إشعار سريع</p>
                  <p className="text-[10px]" style={{ color: textMut }}>يُرسل لجميع المستخدمين</p>
                </div>
              </div>
              <button onClick={() => setOpen(false)}
                className="w-8 h-8 rounded-full flex items-center justify-center"
                style={{ background: isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9', color: textMut }}>
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Target badge */}
            <div className="flex items-center gap-1.5 mb-4 px-3 py-2 rounded-xl"
                 style={{ background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.15)' }}>
              <Users className="w-3.5 h-3.5" style={{ color: '#3b82f6' }} />
              <span className="text-[11px] font-bold" style={{ color: '#3b82f6' }}>جميع المستخدمين المشتركين</span>
            </div>

            {/* Title */}
            <div className="mb-3">
              <label className="block text-[11px] font-bold mb-1.5" style={{ color: textMut }}>العنوان</label>
              <input
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="مثال: مباراة اليوم في دوري روشن"
                maxLength={80}
                className="w-full rounded-xl px-4 py-3 text-sm focus:outline-none transition-colors"
                style={{ background: inputBg, border: `1px solid ${inputBdr}`, color: textPri }}
                dir="rtl"
              />
              <p className="text-[10px] mt-1 text-left" style={{ color: textMut }}>{title.length}/80</p>
            </div>

            {/* Body */}
            <div className="mb-5">
              <label className="block text-[11px] font-bold mb-1.5" style={{ color: textMut }}>نص الإشعار</label>
              <textarea
                value={body}
                onChange={e => setBody(e.target.value)}
                placeholder="اكتب نص الإشعار هنا..."
                maxLength={200}
                rows={3}
                className="w-full rounded-xl px-4 py-3 text-sm focus:outline-none resize-none transition-colors"
                style={{ background: inputBg, border: `1px solid ${inputBdr}`, color: textPri }}
                dir="rtl"
              />
              <p className="text-[10px] mt-1 text-left" style={{ color: textMut }}>{body.length}/200</p>
            </div>

            {/* Preview */}
            {(title || body) && (
              <div className="mb-4 p-3 rounded-xl"
                   style={{ background: isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc', border: `1px solid ${borderClr}` }}>
                <p className="text-[9px] font-bold mb-1.5" style={{ color: textMut }}>معاينة الإشعار</p>
                <div className="flex items-start gap-2.5">
                  <div className="w-8 h-8 rounded-lg shrink-0 flex items-center justify-center"
                       style={{ background: 'rgba(234,179,8,0.15)' }}>
                    <Bell className="w-4 h-4" style={{ color: '#eab308' }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-black truncate" style={{ color: textPri }}>{title || '—'}</p>
                    <p className="text-[10px] leading-relaxed mt-0.5 line-clamp-2" style={{ color: textMut }}>{body || '—'}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Send button */}
            <button
              onClick={handleSend}
              disabled={sending || sent || !title.trim() || !body.trim()}
              data-testid="quick-notif-send"
              className="w-full py-3.5 rounded-2xl font-black text-sm flex items-center justify-center gap-2 transition-all duration-200"
              style={{
                background: sent ? '#22c55e' : (!title.trim() || !body.trim()) ? (isDark ? 'rgba(255,255,255,0.06)' : '#e2e8f0')
                           : 'linear-gradient(135deg, #eab308 0%, #f59e0b 100%)',
                color: sent ? '#fff' : (!title.trim() || !body.trim()) ? textMut : '#000',
                opacity: sending ? 0.8 : 1,
              }}
            >
              {sent ? (
                <><CheckCircle className="w-4 h-4" />أُرسل بنجاح</>
              ) : sending ? (
                <><Loader className="w-4 h-4 animate-spin" />جاري الإرسال...</>
              ) : (
                <><Send className="w-4 h-4" />إرسال الإشعار</>
              )}
            </button>
          </div>
        </>
      )}
    </>
  );
}
